import type { CollapseProps } from 'antd';
import { Button } from 'antd';
import mapValues from 'lodash/mapValues';
import { useState } from 'react';

import { DownOutlined, UpOutlined } from '@ant-design/icons';

import * as St from './SortAndFiltersSection.styles';

import {
  AvailableSortComponents,
  FiltersSectionData,
  SelectedFilterOptions,
} from '~/core/types/sortAndFilter';
import { getSelectedFiltersOptions } from '~/core/utils/sortAndFilter';
import ClearFilter from '~assets/icons/clear-filter.svg';
import FilterOptions, { SelectedOptions } from '~molecules/FilterOptions';
import SelectedFilter from '~molecules/SelectedFilter';
import SortOptions from '~molecules/SortOptions';

export type SortAndFiltersSectionProps = {
  title: string;
  data: FiltersSectionData;
  selectedFilterOptions?: SelectedFilterOptions;
  onChange: (updatedOptions: SelectedFilterOptions) => void;
  uiMode?: 'collapsible-button' | 'expanded';
  isDisabled?: boolean;
  component: AvailableSortComponents;
  setIsFiltersOpen?: (isFiltersOpen: boolean) => void;
  sortedBy?: string;
};

const emptyArray = [] satisfies number[];

const SortAndFiltersSection = ({
  sortedBy,
  title,
  data: { filters },
  onChange,
  selectedFilterOptions: selectedFilterOptionsProp = {},
  uiMode = 'expanded',
  isDisabled = false,
  component,
  setIsFiltersOpen: setIsFiltersOpenProp,
}: SortAndFiltersSectionProps) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const selectedFilterOptions = getSelectedFiltersOptions(
    filters,
    selectedFilterOptionsProp
  );
  const isCollapsibleButton = uiMode === 'collapsible-button';

  const handleCheckboxChange =
    (currentCat: string) => (updatedOptions: SelectedOptions) =>
      onChange({ ...selectedFilterOptions, [currentCat]: updatedOptions });

  const handleClearOptions = () =>
    onChange(mapValues(selectedFilterOptions, () => []));

  const sortByItems: CollapseProps['items'] = [
    {
      key: 0,
      label: 'Sort by',
      children: <SortOptions component={component} filters={filters} />,
    },
  ];

  const filterItems: CollapseProps['items'] = filters.map(
    ([filterCategory, options], index) => ({
      key: index + 1,
      label: filterCategory,
      children: (
        <FilterOptions
          title={filterCategory}
          data={{ options }}
          selectedOptions={selectedFilterOptions[filterCategory] ?? []}
          onChange={handleCheckboxChange(filterCategory)}
        />
      ),
    })
  );

  const items: CollapseProps['items'] = [...sortByItems, ...filterItems];
  const toggleFilterSection = () => {
    setIsFiltersOpen(!isFiltersOpen);
    setIsFiltersOpenProp?.(!isFiltersOpen);
  };

  const titleWithButton = 'Sort & Filters';

  const getSelectedFiltersNames = () =>
    Object.values(selectedFilterOptions).flat();

  const handleRemoveFilter = (filterNameToRemove: string) => {
    const updatedSelectedFilterOptions = Object.entries(
      selectedFilterOptions
    ).reduce<SelectedFilterOptions>((acc, [category, options]) => {
      const filteredOptions = options.filter(
        (optionName) => optionName !== filterNameToRemove
      );

      acc[category] = filteredOptions;

      return acc;
    }, {});

    onChange(updatedSelectedFilterOptions);
  };

  return (
    <>
      {isCollapsibleButton && !isFiltersOpen && (
        <St.CollapsedFilterHeader data-use-button={isCollapsibleButton}>
          <St.OpenFiltersBtn
            onClick={toggleFilterSection}
            disabled={isDisabled}
          >
            {titleWithButton} <DownOutlined />
          </St.OpenFiltersBtn>
          {sortedBy && (
            <SelectedFilter
              key="sorted-by-badge"
              filterName={`Sorted by: ${sortedBy}`}
            />
          )}

          {getSelectedFiltersNames().map((filterName) => (
            <SelectedFilter
              key={filterName}
              filterName={filterName}
              onRemove={() => handleRemoveFilter(filterName)}
            />
          ))}
        </St.CollapsedFilterHeader>
      )}
      {((isCollapsibleButton && isFiltersOpen) || !isCollapsibleButton) && (
        <St.Container data-use-button={isCollapsibleButton}>
          <St.ExpandedFilterHeader>
            <St.FilterTitle>
              {isCollapsibleButton ? (
                <>
                  {titleWithButton}
                  <Button icon={<UpOutlined />} onClick={toggleFilterSection} />
                </>
              ) : (
                title
              )}
            </St.FilterTitle>
            <Button
              ghost
              icon={<ClearFilter />}
              aria-label="clear options"
              onClick={handleClearOptions}
            />
          </St.ExpandedFilterHeader>
          <St.Collapse
            ghost
            expandIconPosition={'end'}
            size={'middle'}
            items={items}
            defaultActiveKey={
              isCollapsibleButton
                ? emptyArray
                : Array.from({ length: items.length }, (_, i) => i)
            }
          />
        </St.Container>
      )}
    </>
  );
};

export default SortAndFiltersSection;
